import cn from 'classnames';
import { compact, flatten, isEmpty, uniqBy } from 'lodash';
import { useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, InputSearch, Selector, TableListV2, useResizeObserver } from 'sonata-react-components';
import { normalize, normalizeWithUnit } from 'sonata-static-storage/lib/helpers/i18nUnits.helpers';
import { worldCountries } from 'sonata-static-storage/lib/samples/worldCountries.samples';

import { AudienceTargetingListStyled } from './AudienceTargetingList.styled';
import { ListSkeleton } from './ListSkeleton';

import type { SelectorOptionPropsInterface } from 'sonata-react-components';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { AudienceGroup } from '../AudienceTargeting';

type State = {
  searchText: string[];
  selectedOptions: SelectorOptionPropsInterface[];
  selectedSegments: string[];
  tableKey: number;
};

type Props = {
  audienceSegments: Record<any, string>[];
  audienceSegmentsGroups: AudienceGroup[];
  baseCurrency: BaseCurrencyInterface;
  isLoading: boolean;
  onClickButton: (ids: string[], btnType: string) => void;
  selectedGroup: AudienceGroup | null;
};

type FormattedSegment = {
  audience: string;
  buttons: Record<string, any>;
  country: string;
  fee: string;
  feeValue: number;
  id: string;
  name: {
    description: Record<string, any>[];
    name: string;
  };
  numOfIds: number;
  provider: string;
};

type SortableRow = {
  original: FormattedSegment;
};

const PAGE_SIZE = 20;
const LIST_MIN_HEIGHT = 384;
const COLUMNS_HEADER_HEIGHT = 40;

const sortByName = (rowA: SortableRow, rowB: SortableRow) =>
  rowA.original.name.name.localeCompare(rowB.original.name.name);

const sortByAudience = (rowA: SortableRow, rowB: SortableRow) => rowA.original.numOfIds - rowB.original.numOfIds;

const sortByFee = (rowA: SortableRow, rowB: SortableRow) => rowA.original.feeValue - rowB.original.feeValue;

function CustomCell({ cell }: { cell: any }) {
  const { props, value } = cell;
  return (
    <div {...props} title={value}>
      {value}
    </div>
  );
}

function TitleCell({ cell }: { cell: any }) {
  const { props, value } = cell;

  const description = value.description.map((val: Record<string, any>) => val.name).join(', ');
  return (
    <div className="audienceTargetingList__nameContainer" {...props}>
      <span className="audienceTargetingList__name" title={value.name}>
        {value.name}
      </span>
      <span className="audienceTargetingList__description" title={description}>
        {description}
      </span>
    </div>
  );
}

function ButtonsCell({
  cell,
  onButtonClick,
  selectedGroup,
  selectedSegments,
  selectedSegmentsExcludedGroup
}: {
  cell: any;
  onButtonClick: (ids: string[], btnType: string) => void;
  selectedGroup: AudienceGroup | null;
  selectedSegments: string[];
  selectedSegmentsExcludedGroup: string[];
}) {
  const { props, value } = cell;

  const selectedSegmentsIncludedGroup = selectedGroup?.segments?.map(s => s.id);
  const isActiveIncluded = selectedSegmentsIncludedGroup?.includes(value.id);
  const isActiveExcluded = selectedSegmentsExcludedGroup?.includes(value.id);

  const showButtons = !selectedSegments.includes(value.id);

  return (
    <div {...props} className="audienceTargetingList__buttonsContainer">
      {showButtons && (
        <>
          <ButtonUI
            className={cn('audienceTargetingList__button', {
              'audienceTargetingList__button--excluded': isActiveExcluded
            })}
            icon="fa-solid fa-minus"
            iconPosition="left"
            id="audienceTargetingList-excludeButton"
            onClick={() => onButtonClick([value.id], 'excluded')}
            shape="squareRounded"
            size="small"
            type="tertiary"
          />
          <ButtonUI
            className={cn('audienceTargetingList__button', {
              'audienceTargetingList__button--included': isActiveIncluded
            })}
            icon="fa-solid fa-plus"
            iconPosition="left"
            id="audienceTargetingList-includeButton"
            onClick={() => onButtonClick([value.id], 'included')}
            shape="squareRounded"
            size="small"
            type="tertiary"
          />
        </>
      )}
    </div>
  );
}

function AudienceTargetingList({
  audienceSegments,
  audienceSegmentsGroups,
  baseCurrency,
  isLoading,
  onClickButton,
  selectedGroup
}: Props) {
  const { formatMessage } = useIntl();

  const [{ searchText, selectedOptions, selectedSegments, tableKey }, setState] = useState<State>({
    searchText: [],
    selectedOptions: [],
    selectedSegments: [],
    tableKey: 0
  });

  const selectedSegmentsExcludedGroup: string[] = flatten(
    audienceSegmentsGroups.filter(group => group.type === 'excluded').map(group => group.segments?.map(s => s.id))
  ).filter((id): id is string => typeof id === 'string');

  const onChangeSelection = (selectedItems: Record<string, any>[]) => {
    const segmentsSelected = selectedItems.map(item => item.id);
    setState(prevState => ({
      ...prevState,
      selectedSegments: segmentsSelected
    }));
  };

  const filterAudienceSegments = useMemo(() => {
    const textFilteredSegments =
      searchText?.length > 0
        ? audienceSegments?.filter((segment: Record<string, any>) => {
            return searchText.some(text => {
              const textLowerCase = text.toLowerCase();
              return (
                segment.name.toLowerCase().includes(textLowerCase) ||
                segment.providers.some((provider: string) => {
                  return provider.includes(textLowerCase);
                }) ||
                segment.categories.some((cat: Record<string, any>) => {
                  return cat.name.toLowerCase().includes(textLowerCase);
                })
              );
            });
          })
        : audienceSegments;
    return selectedOptions.length
      ? textFilteredSegments.filter((segment: Record<string, any>) => {
          return segment.providers.some((provider: string) => {
            const filterValues = selectedOptions.map(opt => opt.id);
            if (filterValues.length === 0) {
              return true;
            }
            return filterValues.includes(provider);
          });
        })
      : textFilteredSegments;
  }, [searchText, audienceSegments, selectedOptions]);

  const columns = useMemo(() => {
    return compact([
      {
        Cell: ({ ...cell }) => <TitleCell cell={cell} />,
        Header: formatMessage({
          defaultMessage: 'Variable name',
          id: 'targeting.audienceTargeting.audienceList.list.columns.variableName'
        }),
        accessor: 'name',
        className: 'audienceTargetingList__cell audienceTargetingList__cell--name',
        sortType: sortByName
      },
      {
        Cell: ({ ...cell }) => <CustomCell cell={cell} />,
        Header: formatMessage({
          defaultMessage: 'Country',
          id: 'targeting.audienceTargeting.audienceList.list.columns.country'
        }),
        accessor: 'country',
        className: 'audienceTargetingList__cell audienceTargetingList__cell--country'
      },
      {
        Cell: ({ ...cell }) => <CustomCell cell={cell} />,
        Header: formatMessage({
          defaultMessage: 'Provider',
          id: 'targeting.audienceTargeting.audienceList.list.columns.provider'
        }),
        accessor: 'provider',
        className: 'audienceTargetingList__cell audienceTargetingList__cell--provider'
      },
      {
        Cell: ({ ...cell }) => <CustomCell cell={cell} />,
        Header: formatMessage({
          defaultMessage: 'Audience',
          id: 'targeting.audienceTargeting.audienceList.list.columns.audience'
        }),
        accessor: 'audience',
        className: 'audienceTargetingList__cell audienceTargetingList__cell--audience',
        sortType: sortByAudience
      },
      {
        Cell: ({ ...cell }) => <CustomCell cell={cell} />,
        Header: formatMessage({
          defaultMessage: 'Fee',
          id: 'targeting.audienceTargeting.audienceList.list.columns.fee'
        }),
        accessor: 'fee',
        className: 'audienceTargetingList__cell audienceTargetingList__cell--fee',
        sortType: sortByFee
      },
      {
        Cell: ({ ...cell }) => (
          <ButtonsCell
            cell={cell}
            onButtonClick={onClickButton}
            selectedGroup={selectedGroup}
            selectedSegments={selectedSegments}
            selectedSegmentsExcludedGroup={selectedSegmentsExcludedGroup}
          />
        ),
        Header: '',
        accessor: 'buttons',
        className: 'audienceTargetingList__cell audienceTargetingList__cell--buttons',
        disableSortBy: true
      }
    ]);
  }, [selectedGroup, selectedSegmentsExcludedGroup, selectedSegments, formatMessage, onClickButton]);

  const formattedData = useMemo(() => {
    const getNormalizedAudience = (numOfIds: number) => {
      if (numOfIds === -1) {
        return 'N/A';
      }
      return `${normalize(numOfIds, 0, baseCurrency?.countries![0])} aprox`;
    };

    return filterAudienceSegments?.map((item: Record<string, any>): FormattedSegment => {
      return {
        audience: getNormalizedAudience(item.numOfIds),
        buttons: item,
        country: item.countries
          .map((c: string) => {
            const country = worldCountries.find(wc => wc.cca2.toLowerCase() === c);
            return country?.name.common || 'Unknown Country';
          })
          .join(', '),
        fee: normalizeWithUnit(item.defaultPrice.cpm * 1000, baseCurrency, 2),
        feeValue: item.defaultPrice.cpm,
        id: item.id,
        name: {
          description: item.categories,
          name: item.name
        },
        numOfIds: item.numOfIds,
        provider: item.providers
          .map((provider: string) => provider.charAt(0).toUpperCase() + provider.slice(1))
          .join(', ')
      };
    });
  }, [filterAudienceSegments, baseCurrency]);

  const getProvidersOptions = (): SelectorOptionPropsInterface[] => {
    return uniqBy(
      flatten(
        audienceSegments?.map((item: Record<string, any>) => {
          return item.providers.map((provider: string) => ({
            id: provider,
            label: provider.charAt(0).toUpperCase() + provider.slice(1)
          }));
        })
      ),
      'id'
    );
  };

  const handleInputSearchChange = (text: string) => {
    const searchValues = text.length ? text.split(',').map((a: string) => a.trim()) : [];
    setState(prevState => ({
      ...prevState,
      searchText: searchValues
    }));
  };

  const handleSelectorChange = (values: SelectorOptionPropsInterface[]) => {
    setState(prevState => ({
      ...prevState,
      selectedOptions: values
    }));
  };

  const handleButtonClick = (ids: string[], type: string) => {
    onClickButton(ids, type);
    setState(prevState => ({
      ...prevState,
      selectedSegments: [],
      tableKey: prevState.tableKey + 1
    }));
  };

  const hasSelectedSegments = selectedSegments.length > 0;

  // The list fills the height left in the box, which grows with the groups panel next to it
  const listContainerRef = useRef<HTMLDivElement>(null);
  const { height: listContainerHeight } = useResizeObserver(listContainerRef, String(isLoading));
  const listMaxHeight = Math.max(LIST_MIN_HEIGHT, Math.floor(listContainerHeight) - COLUMNS_HEADER_HEIGHT);

  return (
    <AudienceTargetingListStyled className="audienceTargetingList">
      {isLoading ? (
        <ListSkeleton />
      ) : (
        <>
          <div className="audienceTargetingList__filters">
            <Selector
              className="audienceTargetingList__selector"
              onChange={handleSelectorChange}
              options={getProvidersOptions()}
              placeholder={formatMessage({
                defaultMessage: 'All providers',
                id: 'targeting.audienceTargeting.audienceList.filters.providers'
              })}
              showSearch={false}
              showSelectAll
              showSelectedItems={false}
              tiny
              type="multiple"
              withGrouping
            />
            <InputSearch
              automaticSearch={false}
              className="audienceTargetingList__inputSearch"
              id="audienceTargetingList-inputSearch"
              lens
              name="audienceTargetingList-search"
              onChange={val => handleInputSearchChange(val)}
              placeholder={formatMessage({
                defaultMessage: 'Search by name...',
                id: 'targeting.audienceTargeting.audienceList.filters.inputSearch'
              })}
              tiny
            />
            <ButtonUI
              disabled={!hasSelectedSegments}
              icon="fa-solid fa-minus"
              iconPosition="left"
              id="audienceTargetingList-bulkExcludeButton"
              onClick={() => handleButtonClick(selectedSegments, 'excluded')}
              shape="squareRounded"
              size="ultraSmall"
              text={formatMessage({
                defaultMessage: 'Exclude',
                id: 'targeting.audienceTargeting.audienceList.buttons.excluded'
              })}
              type="secondary"
            />
            <ButtonUI
              disabled={!hasSelectedSegments}
              icon="fa-solid fa-plus"
              iconPosition="left"
              id="audienceTargetingList-bulkIncludeButton"
              onClick={() => handleButtonClick(selectedSegments, 'included')}
              shape="squareRounded"
              size="ultraSmall"
              text={formatMessage({
                defaultMessage: 'Include',
                id: 'targeting.audienceTargeting.audienceList.buttons.included'
              })}
              type="secondary"
            />
          </div>
          <div className="audienceTargetingList__listContainer" ref={listContainerRef}>
            <div className="audienceTargetingList__listContent">
              <TableListV2
                className="audienceTargetingList__list"
                columns={columns}
                config={{
                  emptyState: {
                    description: formatMessage({
                      defaultMessage: "Try adjusting your search or filters to find what you're looking for.",
                      id: 'targeting.audienceTargeting.audienceList.list.emptyStateDescription'
                    }),
                    icon: 'fal fa-empty-set',
                    show: isEmpty(formattedData),
                    title: formatMessage({
                      defaultMessage: 'No Results Found',
                      id: 'targeting.audienceTargeting.audienceList.list.emptyStateTitle'
                    })
                  },
                  maxHeight: listMaxHeight,
                  onChangeSelection: items => onChangeSelection(items),
                  pagination: {
                    lazyLoad: {
                      enabled: true,
                      threshold: 0.75
                    },
                    show: true,
                    size: PAGE_SIZE,
                    type: 'compact'
                  },
                  type: 'list'
                }}
                data={formattedData}
                id="audienceTargetingList"
                isSelectableList
                key={tableKey}
                showColumnsHeader
              />
            </div>
          </div>
        </>
      )}
    </AudienceTargetingListStyled>
  );
}

export default AudienceTargetingList;
